import type { ActivityLap, ActivityRecord, Pause } from '@fit-editor/fit-core'

import type { EditedRange } from '@/lib/edit-ranges'
import type { UnitSystem } from '@/lib/format'
import {
  buildMetricSeriesData,
  elevationDisplay,
  getAvailableMetrics,
  METRIC_COLORS,
  metricDisplay,
} from '@/lib/timeline-metrics'
import type { MetricDef } from '@/lib/timeline-metrics'
import { getThemeColors, withAlpha } from '@/lib/theme-colors'
import type { ThemeColors } from '@/lib/theme-colors'
import type { Theme, TimelineLayout } from '@/store/activity-store'

/**
 * Pure ECharts `option` builder for the timeline. Kept free of any
 * ECharts/React import so it's unit-testable as plain data -- assert on
 * the shape of the returned object rather than mounting a real chart
 * (jsdom has no canvas, so nothing here can verify actual pixels; that's
 * a human/browser verification step, not a unit test concern).
 */

export interface BuildTimelineOptionArgs {
  records: ActivityRecord[]
  laps: ActivityLap[]
  pauses: Pause[]
  editedRanges: EditedRange[]
  theme: Theme
  hoveredTimestampMs: number | null
  minTimeMs: number
  maxTimeMs: number
  /** Split points picked so far in the split dialog (M6) -- rendered as distinct marker lines, not conflated with lap boundaries or edited ranges. */
  splitPointsMs?: number[]
  /**
   * Terrain elevation overlay (session-only, not an edit), index-aligned to
   * `records`; `null` entries are gaps. When present, rendered as its own
   * dashed series on the Altitude axis (if Altitude exists) so the two are
   * directly comparable.
   */
  terrainValues?: (number | null)[] | undefined
  /**
   * `'combined'` (default): every metric overlaid on one plot with its own
   * y-axis. `'stacked'`: one panel (grid) per metric, sharing the time axis.
   */
  layout?: TimelineLayout
  /**
   * Stacked mode only: whether a series (by its legend name) is currently
   * toggled on in the legend. The stacked tooltip lists every panel's series
   * itself (rather than trusting ECharts to report series from grids other
   * than the hovered one), so it needs this to skip legend-hidden ones. The
   * legend's selection lives inside the ECharts instance, so the component
   * supplies a callback reading it rather than baking it into the option
   * (which would force a full option rebuild on every legend click).
   */
  isSeriesVisible?: (seriesName: string) => boolean
  /**
   * Display unit system (default metric): speed in km/h or mph, altitude and
   * terrain in m or ft. Applied to the chart's own copy of the series data
   * (so the exact-value tooltip shows converted values too) -- never to the
   * activity itself.
   */
  units?: UnitSystem
  /**
   * Stacked mode only: the measured height (px) of the box the chart fills.
   * Every panel is sized to fit inside it (`computeStackedGeometry`) -- the
   * chart never scrolls. Absent: `STACKED_LAYOUT.fallbackHeight`.
   */
  containerHeight?: number | undefined
}

export const HOVER_SERIES_NAME = '__hover_cursor__'
export const TERRAIN_SERIES_ID = 'terrainElevation'
/** The terrain series' legend/axis name in the given unit system. */
export function terrainSeriesName(units: UnitSystem = 'metric'): string {
  return `Terrain elevation (${elevationDisplay(units).unit})`
}
/** `terrainSeriesName('metric')`. */
export const TERRAIN_SERIES_NAME = terrainSeriesName('metric')

/**
 * Stacked-layout constants, in px.
 *
 * Vertical order, top to bottom (the legend is HTML above the chart --
 * `TimelineLegend` -- not part of the canvas): a row for the lap/split
 * marker labels drawn above the top panel
 * (`markerLabelRow`); then per panel a `nameStrip` holding the metric's
 * name, *above* (outside) the panel's plot rectangle so series lines can
 * never cross it, followed by the plot itself; panels are separated by
 * `panelGap` (`tightPanelGap` once plots drop below `minPanelHeight`).
 * `bottom` leaves room for the bottom panel's time labels plus the zoom
 * slider, which is always visible.
 *
 * Each plot's y axis shows only its min and max value, drawn *inside* the
 * plot's vertical extent (max hangs down from the top edge, min sits on
 * the bottom edge), so a panel's labels can't reach the panel above's or
 * below's; they're dropped on plots shorter than `minLabelledPanelHeight`
 * (where the two would touch). Plots shorter than `minNamedPanelHeight`
 * even with tight gaps also drop the name strips -- the legend and tooltip
 * still identify every series. `preferredPanelHeight` is the plot height
 * the default (auto-fit) timeline height is sized for
 * (`stackedChartHeightFor`).
 */
export const STACKED_LAYOUT = {
  markerLabelRow: 14,
  nameStrip: 16,
  bottom: 50,
  panelGap: 6,
  tightPanelGap: 2,
  minPanelHeight: 28,
  minNamedPanelHeight: 8,
  minLabelledPanelHeight: 24,
  preferredPanelHeight: 48,
  fallbackHeight: 220,
} as const

/** Offset of the top panel's name strip: below the marker-label row. */
export const STACKED_TOP = STACKED_LAYOUT.markerLabelRow

/** ECharts' own default markLine label distance, used when there are no name strips. */
const DEFAULT_MARKER_LABEL_DISTANCE = 5

export interface StackedGeometry {
  /** One plot rectangle per panel, top to bottom. */
  grids: { top: number; height: number }[]
  /**
   * The strip above each plot that holds the panel's name (zero height when
   * names are dropped). Always outside, and directly above, its own plot.
   */
  nameStrips: { top: number; height: number }[]
  panelHeight: number
  /** Whether panel names fit (`nameStrips` have non-zero height). */
  showNames: boolean
  /** Min/max value labels are dropped on very short panels (the tooltip still has exact values). */
  showYAxisLabels: boolean
  /**
   * Distance (px) of the top panel's lap/split labels above its plot: past
   * the name strip, into the marker-label row, so they can't cover the name.
   */
  markerLabelDistance: number
}

function validHeight(containerHeight: number | undefined): number {
  return containerHeight != null && Number.isFinite(containerHeight) && containerHeight > 0
    ? containerHeight
    : STACKED_LAYOUT.fallbackHeight
}

/**
 * Divides a measured container height among `panelCount` stacked panels so
 * that all of them -- plus the marker-label row, the name strips, the time labels
 * and the zoom slider -- fit without scrolling. Short on space, the panels
 * keep shrinking (rather than overflowing): first the gaps tighten, then
 * the y labels go, and finally the name strips.
 */
export function computeStackedGeometry(
  containerHeight: number | undefined,
  panelCount: number,
): StackedGeometry {
  const n = Math.max(1, Math.floor(panelCount))
  const {
    bottom,
    nameStrip,
    panelGap,
    tightPanelGap,
    minPanelHeight,
    minNamedPanelHeight,
    minLabelledPanelHeight,
  } = STACKED_LAYOUT
  const height = validHeight(containerHeight)
  const plotHeight = (strip: number, gap: number) =>
    Math.floor((height - STACKED_TOP - bottom - n * strip - (n - 1) * gap) / n)

  let strip: number = nameStrip
  let gap: number = panelGap
  let panelHeight = plotHeight(strip, gap)
  if (panelHeight < minPanelHeight) {
    gap = tightPanelGap
    panelHeight = plotHeight(strip, gap)
  }
  if (panelHeight < minNamedPanelHeight) {
    strip = 0
    panelHeight = plotHeight(strip, gap)
  }
  panelHeight = Math.max(1, panelHeight)

  const grids = Array.from({ length: n }, (_, i) => ({
    top: STACKED_TOP + i * (strip + panelHeight + gap) + strip,
    height: panelHeight,
  }))
  return {
    grids,
    nameStrips: grids.map((g) => ({ top: g.top - strip, height: strip })),
    panelHeight,
    showNames: strip > 0,
    showYAxisLabels: panelHeight >= minLabelledPanelHeight,
    markerLabelDistance: strip > 0 ? strip + 2 : DEFAULT_MARKER_LABEL_DISTANCE,
  }
}

/**
 * Chart height (px) at which `panelCount` stacked panels each get a
 * `preferredPanelHeight` plot, with full gaps and name strips -- what the
 * timeline grows to by default in stacked mode (capped by the caller).
 */
export function stackedChartHeightFor(panelCount: number): number {
  const n = Math.max(1, Math.floor(panelCount))
  const { bottom, nameStrip, panelGap, preferredPanelHeight } = STACKED_LAYOUT
  return STACKED_TOP + bottom + n * (nameStrip + preferredPanelHeight) + (n - 1) * panelGap
}

/**
 * Compact y-axis value label: at most one decimal, `k` from 10 000 and `M`
 * from 10 000 000, no thousands separator -- keeps every label narrow
 * enough for the fixed axis margins (metric or imperial).
 */
export function formatAxisValue(value: number): string {
  if (!Number.isFinite(value)) return ''
  const oneDecimal = (v: number) => String(Math.round(v * 10) / 10)
  const abs = Math.abs(value)
  if (abs >= 1e7) return `${oneDecimal(value / 1e6)}M`
  if (abs >= 1e4) return `${oneDecimal(value / 1e3)}k`
  return oneDecimal(value)
}

/** 1, 2 or 5 times a power of ten, never below 0.1 (so bounds need at most one decimal). */
function niceStep(rough: number): number {
  const exponent = Math.floor(Math.log10(rough))
  const fraction = rough / 10 ** exponent
  const nice = [1, 2, 5].find((candidate) => fraction <= candidate) ?? 10
  return Math.max(0.1, nice * 10 ** exponent)
}

/**
 * Rounded-out y-axis bounds enclosing `[lo, hi]` (multiples of a 1/2/5
 * step about a fifth of the range), so a panel's two value labels are
 * round numbers. A constant series gets a band around its value. `null`
 * for an empty extent.
 */
export function niceAxisBounds(lo: number, hi: number): { min: number; max: number } | null {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) return null
  if (hi === lo) {
    const half = lo === 0 ? 1 : Math.abs(lo) * 0.1
    return niceAxisBounds(lo - half, hi + half)
  }
  const step = niceStep((hi - lo) / 5)
  const decimals = Math.max(0, -Math.floor(Math.log10(step)))
  const snap = (k: number) => Number((k * step).toFixed(decimals))
  return {
    min: snap(Math.floor(lo / step + 1e-9)),
    max: snap(Math.ceil(hi / step - 1e-9)),
  }
}

/** `[min, max]` of the non-null values across `seriesList`, or `null` if there are none. */
function valueExtent(seriesList: (SeriesPoints | undefined)[]): [number, number] | null {
  let lo = Infinity
  let hi = -Infinity
  for (const points of seriesList) {
    for (const point of points ?? []) {
      const value = point[1]
      if (value == null || !Number.isFinite(value)) continue
      if (value < lo) lo = value
      if (value > hi) hi = value
    }
  }
  return lo <= hi ? [lo, hi] : null
}

/**
 * Number of stacked panels: one per metric, plus one for terrain only when
 * the device recorded no altitude (otherwise terrain shares Altitude's panel).
 */
export function stackedPanelCount(metrics: MetricDef[], hasTerrain: boolean): number {
  const terrainOwnPanel = hasTerrain && !metrics.some((m) => m.key === 'altitude')
  return metrics.length + (terrainOwnPanel ? 1 : 0)
}

/**
 * The terrain series' color: the theme foreground (near-black in light
 * mode, near-white in dark), drawn dashed. This is the Okabe-Ito palette's
 * own "black" entry adapted per theme -- the remaining unused Okabe-Ito
 * hue (yellow #f0e442) has too little contrast on the light card
 * background, and fixed black would vanish in dark mode. Hue-neutral +
 * dashed keeps it unmistakable next to the (reddish-purple) device
 * Altitude line on the shared axis, including for color-blind users.
 */
export function terrainColor(colors: ThemeColors): string {
  return colors.foreground
}

/**
 * `[epochMs, value]` pairs for the terrain overlay, on exactly the same
 * timestamp grid as `buildMetricSeriesData` (one entry per record that has
 * a timestamp, `null` for a gap) -- required for the shared axis tooltip
 * and 'average' sampling to line up with the device series.
 */
export function buildTerrainSeriesData(
  records: ActivityRecord[],
  values: (number | null)[],
  /** Optional metres-to-display conversion (feet), applied to this display copy only. */
  convert?: ((value: number) => number) | null,
): [number, number | null][] {
  const data: [number, number | null][] = []
  for (let i = 0; i < records.length; i++) {
    const timestamp = records[i]?.timestamp
    if (!(timestamp instanceof Date)) continue
    const value = values[i]
    data.push([
      timestamp.getTime(),
      typeof value !== 'number' ? null : convert != null ? convert(value) : value,
    ])
  }
  return data
}

export interface HoverMarkLineArgs {
  laps: ActivityLap[]
  hoveredTimestampMs: number | null
  splitPointsMs?: number[]
  colors: ThemeColors
}

/**
 * Builds the hidden hover-series `markLine.data` array (lap boundaries +
 * the hovered-timestamp cursor + split-point markers). Pulled out of
 * `buildTimelineOption` so the component can also call it directly for a
 * lightweight, merge-mode `setOption` update driven purely by hover —
 * see `activity-timeline.tsx` for why: rebuilding and re-applying the
 * *entire* chart option on every hover frame (as this used to do) forced
 * ECharts to drop the user's current legend-toggle and zoom/pan state on
 * every mouse move, since neither survives a `notMerge: true` replace
 * unless the new option also happens to describe them.
 */
export function buildHoverMarkLineData(args: HoverMarkLineArgs) {
  const { laps, hoveredTimestampMs, splitPointsMs = [], colors } = args

  const lapMarkLines = laps
    .map((lap, i) => {
      const start = lap.startTime instanceof Date ? lap.startTime.getTime() : undefined
      if (start == null) return null
      return {
        xAxis: start,
        label: { formatter: `Lap ${i + 1}`, color: colors.mutedForeground, fontSize: 10 },
        lineStyle: { type: 'dashed' as const, color: colors.mutedForeground, width: 1 },
      }
    })
    .filter((v): v is NonNullable<typeof v> => v != null)

  const hoverMarkLine =
    hoveredTimestampMs == null
      ? []
      : [
          {
            xAxis: hoveredTimestampMs,
            label: { show: false },
            lineStyle: { type: 'solid' as const, color: colors.primary, width: 2 },
          },
        ]

  const splitMarkLines = splitPointsMs.map((ms) => ({
    xAxis: ms,
    label: { formatter: 'Split', color: colors.warning, fontSize: 10, position: 'end' as const },
    lineStyle: { type: 'solid' as const, color: colors.warning, width: 2 },
  }))

  return [...lapMarkLines, ...hoverMarkLine, ...splitMarkLines]
}

type MarkLineEntry = ReturnType<typeof buildHoverMarkLineData>[number]

/**
 * Same marker positions with their text labels hidden -- used for every
 * stacked panel except the top one, so "Lap 3" / "Pause" / "Split" text is
 * shown once rather than repeated in each panel, while the lines and
 * shaded areas themselves still appear in every panel.
 */
function withoutLabels<T extends object>(entry: T): T {
  return { ...entry, label: { show: false } }
}

/**
 * Ids of the hidden annotation series in an option built by
 * `buildTimelineOption`: `[HOVER_SERIES_NAME]` in combined mode,
 * `HOVER_SERIES_NAME:0`, `:1`, ... (one per panel, top first) in stacked.
 */
export function annotationSeriesIds(option: Record<string, unknown>): string[] {
  const series = (option.series ?? []) as { id?: unknown }[]
  return series
    .map((s) => s.id)
    .filter(
      (id): id is string =>
        typeof id === 'string' &&
        (id === HOVER_SERIES_NAME || id.startsWith(`${HOVER_SERIES_NAME}:`)),
    )
}

/**
 * The merge-mode `series` patch that applies `markLineData` (laps + hover
 * cursor + split points) to every annotation series -- the first (top)
 * one keeps its labels, the rest get label-less copies.
 */
export function buildHoverSeriesPatch(seriesIds: string[], markLineData: MarkLineEntry[]) {
  return seriesIds.map((id, i) => ({
    id,
    markLine: { data: i === 0 ? markLineData : markLineData.map(withoutLabels) },
  }))
}

type SeriesPoints = [number, number | null][]

/** Value of the record nearest to `timeMs` (binary search; points are in time order). */
export function valueNearest(points: SeriesPoints, timeMs: number): number | null {
  if (points.length === 0) return null
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if ((points[mid]?.[0] ?? 0) < timeMs) lo = mid + 1
    else hi = mid
  }
  const after = points[lo]
  const before = points[lo - 1]
  const nearest =
    before != null && after != null && timeMs - before[0] < after[0] - timeMs ? before : after
  return nearest?.[1] ?? null
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
}

interface TooltipParam {
  seriesId?: string
  seriesName?: string
  marker?: string
  axisValue?: number
  axisValueLabel?: string
}

function tooltipRow(marker: string, name: string, points: SeriesPoints, timeMs: number): string {
  const value = valueNearest(points, timeMs)
  const shown = value == null ? '-' : String(Math.round(value * 100) / 100)
  return `${marker}${escapeHtml(name)}<span style="float:right;margin-left:16px;font-weight:600">${shown}</span>`
}

/**
 * Axis tooltip showing each visible series' *exact* record value at the
 * hovered time, looked up from the raw points rather than taken from the
 * downsampled point ECharts snapped to (which can be a bucket average).
 */
function formatTooltip(params: unknown, seriesDataByKey: Map<string, SeriesPoints>): string {
  const list = (Array.isArray(params) ? params : [params]) as TooltipParam[]
  const first = list[0]
  if (first?.axisValue == null) return ''
  const rows = list
    .filter((p) => p.seriesId != null && seriesDataByKey.has(p.seriesId))
    .map((p) =>
      tooltipRow(
        p.marker ?? '',
        p.seriesName ?? '',
        seriesDataByKey.get(p.seriesId as string) ?? [],
        first.axisValue as number,
      ),
    )
  return [escapeHtml(first.axisValueLabel ?? ''), ...rows].join('<br/>')
}

interface TooltipSeriesEntry {
  id: string
  name: string
  color: string
}

function colorMarker(color: string): string {
  return `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${escapeHtml(color)};"></span>`
}

/**
 * Stacked-mode tooltip: lists *every* visible series in panel order at the
 * hovered time, not only those ECharts reports in `params` -- with several
 * grids, ECharts' axis tooltip may report only the hovered grid's series,
 * so `params` is used just for the hovered time and its label.
 */
function formatStackedTooltip(
  params: unknown,
  entries: TooltipSeriesEntry[],
  seriesDataByKey: Map<string, SeriesPoints>,
  isSeriesVisible: (seriesName: string) => boolean,
): string {
  const list = (Array.isArray(params) ? params : [params]) as TooltipParam[]
  const first = list.find((p) => p?.axisValue != null)
  if (first?.axisValue == null) return ''
  const rows = entries
    .filter((e) => isSeriesVisible(e.name))
    .map((e) =>
      tooltipRow(
        colorMarker(e.color),
        e.name,
        seriesDataByKey.get(e.id) ?? [],
        first.axisValue as number,
      ),
    )
  return [escapeHtml(first.axisValueLabel ?? ''), ...rows].join('<br/>')
}

interface StackedLayoutArgs {
  geometry: StackedGeometry
  timeAxisBase: Record<string, unknown> & { axisLabel: Record<string, unknown> }
  axisTitles: string[]
  axisColors: string[]
  colors: ThemeColors
  /** Per panel, the series data plotted on it (for its y-axis bounds). */
  panelData: (SeriesPoints | undefined)[][]
}

/** `grid`/`xAxis`/`yAxis`/`axisPointer` of the stacked layout -- one panel per axis. */
function buildStackedLayout(args: StackedLayoutArgs) {
  const { geometry, timeAxisBase, axisTitles, axisColors, colors, panelData } = args
  const panelCount = geometry.grids.length
  const lastIndex = panelCount - 1
  return {
    grid: geometry.grids.map((g) => ({
      left: 52,
      right: 16,
      top: g.top,
      height: g.height,
      // Never let ECharts shrink one panel's rect to fit its labels: every
      // panel must keep exactly the same x extent so the shared time axes
      // line up (and the geometry above already leaves room for labels).
      outerBoundsMode: 'none',
    })),
    xAxis: Array.from({ length: panelCount }, (_, i) => ({
      ...timeAxisBase,
      gridIndex: i,
      // Only the bottom panel labels the shared time axis.
      axisLabel: { ...timeAxisBase.axisLabel, show: i === lastIndex },
      axisTick: { show: i === lastIndex },
    })),
    yAxis: Array.from({ length: panelCount }, (_, i) => {
      const extent = valueExtent(panelData[i] ?? [])
      const bounds = extent != null ? niceAxisBounds(extent[0], extent[1]) : null
      return {
        type: 'value',
        gridIndex: i,
        scale: true,
        // Exactly two ticks -- the rounded-out min and max -- so each panel
        // shows just those two value labels however short it gets.
        ...(bounds != null
          ? { min: bounds.min, max: bounds.max, interval: bounds.max - bounds.min }
          : { splitNumber: 1 }),
        // The name sits in the strip *above* the plot (bottom-aligned,
        // just over the axis end), never inside it, so lines can't cross it.
        name: geometry.showNames ? (axisTitles[i] ?? '') : '',
        nameLocation: 'end',
        nameGap: 2,
        nameTextStyle: {
          color: axisColors[i] ?? colors.foreground,
          align: 'left',
          verticalAlign: 'bottom',
          fontWeight: 600,
          fontSize: 11,
          lineHeight: 13,
        },
        axisLine: { show: true, lineStyle: { color: axisColors[i] ?? colors.border } },
        axisLabel: {
          show: geometry.showYAxisLabels,
          color: colors.mutedForeground,
          fontSize: 10,
          hideOverlap: true,
          showMinLabel: true,
          showMaxLabel: true,
          // Kept within the plot's own vertical extent (max hangs down from
          // the top edge, min sits on the bottom edge), so they can never
          // meet the neighbouring panels' labels.
          verticalAlignMaxLabel: 'top',
          verticalAlignMinLabel: 'bottom',
          formatter: formatAxisValue,
        },
        splitLine: { show: true, lineStyle: { color: colors.border } },
      }
    }),
    // Hovering any panel moves the axis pointer in all of them.
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
  }
}

/** One toggle in the timeline's HTML legend. */
export interface TimelineLegendEntry {
  /** Series (= ECharts legend) name. */
  name: string
  color: string
  /** Drawn dashed (terrain elevation). */
  dashed: boolean
}

/** The legend toggles for an option from `buildTimelineOption`, in legend order. */
export function timelineLegendEntries(option: Record<string, unknown>): TimelineLegendEntry[] {
  const names = (option.legend as { data?: string[] } | undefined)?.data ?? []
  const series =
    (option.series as { name?: string; color?: string; lineStyle?: { type?: string } }[]) ?? []
  return names.map((name) => {
    const s = series.find((candidate) => candidate.name === name)
    return { name, color: s?.color ?? 'currentColor', dashed: s?.lineStyle?.type === 'dashed' }
  })
}

export function buildTimelineOption(args: BuildTimelineOptionArgs): Record<string, unknown> {
  const {
    records,
    laps,
    pauses,
    editedRanges,
    theme,
    hoveredTimestampMs,
    minTimeMs,
    maxTimeMs,
    splitPointsMs = [],
    terrainValues,
    layout = 'combined',
    isSeriesVisible = () => true,
    units = 'metric',
    containerHeight,
  } = args
  const stacked = layout === 'stacked'
  const colors = getThemeColors(theme)
  const metrics = getAvailableMetrics(records)
  const displays = new Map(metrics.map((m) => [m.key, metricDisplay(m, units)]))
  /** Legend/axis/tooltip name of a metric's series, with its display unit. */
  const seriesName = (m: (typeof metrics)[number]): string =>
    `${m.label} (${displays.get(m.key)?.unit ?? m.unit})`
  const terrainName = terrainSeriesName(units)
  const altitudeIndex = metrics.findIndex((m) => m.key === 'altitude')
  const hasTerrain = terrainValues != null
  // Terrain shares the device Altitude axis (and, stacked, its panel) when
  // there is one -- so the two are directly comparable -- and only gets its
  // own axis/panel otherwise.
  const terrainOwnAxis = hasTerrain && altitudeIndex < 0
  const terrainAxisIndex = altitudeIndex >= 0 ? altitudeIndex : metrics.length
  const axisColors: string[] = [
    ...metrics.map((m) => METRIC_COLORS[m.key]),
    ...(terrainOwnAxis ? [terrainColor(colors)] : []),
  ]
  const axisTitles: string[] = [
    ...metrics.map(seriesName),
    ...(terrainOwnAxis ? [terrainName] : []),
  ]
  // In combined mode there is one grid; in stacked mode one per axis.
  const panelCount = stacked ? Math.max(1, axisColors.length) : 1
  const geometry = stacked ? computeStackedGeometry(containerHeight, panelCount) : null

  const pauseMarkAreas = pauses.map((pause) => [
    {
      xAxis: pause.start.getTime(),
      itemStyle: { color: colors.mutedForeground, opacity: 0.15 },
      label: { formatter: 'Pause', color: colors.mutedForeground, position: 'insideTop' as const },
    },
    { xAxis: pause.end.getTime() },
  ])

  const editedMarkAreas = editedRanges.map((range) => [
    {
      xAxis: range.start,
      itemStyle: { color: colors.primary, opacity: 0.12 },
      label: { formatter: range.label, color: colors.primary, position: 'insideTop' as const },
    },
    { xAxis: range.end },
  ])
  const markAreas = [...pauseMarkAreas, ...editedMarkAreas]

  const seriesDataByKey = new Map(
    metrics.map((metric) => [
      metric.key as string,
      buildMetricSeriesData(records, metric.key, displays.get(metric.key)?.convert),
    ]),
  )
  if (terrainValues != null) {
    seriesDataByKey.set(
      TERRAIN_SERIES_ID,
      buildTerrainSeriesData(records, terrainValues, elevationDisplay(units).convert),
    )
  }

  // Each axis index doubles as the panel (grid/xAxis) index in stacked mode.
  const coords = (axisIndex: number) =>
    stacked ? { xAxisIndex: axisIndex, yAxisIndex: axisIndex } : { yAxisIndex: axisIndex }

  const terrainSeries =
    terrainValues == null
      ? []
      : [
          {
            id: TERRAIN_SERIES_ID,
            name: terrainName,
            type: 'line',
            ...coords(terrainAxisIndex),
            showSymbol: false,
            large: true,
            sampling: 'average',
            color: terrainColor(colors),
            lineStyle: { type: 'dashed', width: 1.5 },
            data: seriesDataByKey.get(TERRAIN_SERIES_ID),
          },
        ]
  const legendNames = [...metrics.map(seriesName), ...(terrainValues != null ? [terrainName] : [])]

  const metricSeries = metrics.map((metric, i) => ({
    id: metric.key,
    name: seriesName(metric),
    type: 'line',
    ...coords(i),
    showSymbol: false,
    large: true,
    // Not 'lttb': LTTB keeps a different subset of points per series, so
    // at a given hover position only the series that happened to keep a
    // point at that exact timestamp appear in the axis tooltip. 'average'
    // buckets every series identically, keeping them on one shared grid.
    // Exact values are still shown -- see the tooltip formatter below.
    sampling: 'average',
    color: METRIC_COLORS[metric.key],
    data: seriesDataByKey.get(metric.key),
  }))

  // Dedicated zero-data series solely to host the hover cursor / lap /
  // split / pause / edited-range annotations, which must be drawn
  // regardless of which (if any) metric series are toggled on/off in the
  // legend -- excluded from `legend.data` so it never appears as a toggle.
  // Stacked mode gets one per panel (same marker positions; text labels
  // only on the top panel).
  const markLineData = buildHoverMarkLineData({ laps, hoveredTimestampMs, splitPointsMs, colors })
  const annotationSeries = Array.from({ length: panelCount }, (_, i) => ({
    id: stacked ? `${HOVER_SERIES_NAME}:${i}` : HOVER_SERIES_NAME,
    name: HOVER_SERIES_NAME,
    type: 'line',
    ...(stacked ? { xAxisIndex: i, yAxisIndex: i } : {}),
    data: [],
    silent: true,
    tooltip: { show: false },
    markLine: {
      symbol: 'none',
      silent: true,
      animation: false,
      // Stacked: lift the top panel's lap/split labels past its name strip
      // (series-level default, so the hover patch's data items inherit it).
      ...(geometry != null && i === 0 ? { label: { distance: geometry.markerLabelDistance } } : {}),
      data: i === 0 ? markLineData : markLineData.map(withoutLabels),
    },
    markArea: {
      silent: true,
      data: i === 0 ? markAreas : markAreas.map(([a, b]) => [withoutLabels(a as object), b]),
    },
  }))

  const series = [...metricSeries, ...terrainSeries, ...annotationSeries]

  const timeAxisBase = {
    type: 'time',
    min: Number.isFinite(minTimeMs) ? minTimeMs : undefined,
    max: Number.isFinite(maxTimeMs) ? maxTimeMs : undefined,
    // Drop labels that would collide (narrow phone-width charts) rather than overprint them.
    axisLabel: { color: colors.mutedForeground, hideOverlap: true },
    axisLine: { lineStyle: { color: colors.border } },
    splitLine: { show: false },
  }

  const tooltipEntries: TooltipSeriesEntry[] = [
    ...metrics.map((m) => ({
      id: m.key as string,
      name: seriesName(m),
      color: METRIC_COLORS[m.key],
    })),
    ...(terrainValues != null
      ? [{ id: TERRAIN_SERIES_ID, name: terrainName, color: terrainColor(colors) }]
      : []),
  ]
  // Panel order: terrain right after Altitude when it shares that panel.
  if (stacked && terrainValues != null && altitudeIndex >= 0) {
    const terrainEntry = tooltipEntries.pop()
    if (terrainEntry != null) tooltipEntries.splice(altitudeIndex + 1, 0, terrainEntry)
  }

  const layoutParts =
    geometry != null
      ? buildStackedLayout({
          geometry,
          timeAxisBase,
          axisTitles,
          axisColors,
          colors,
          // Each panel's data: its metric, plus terrain on the panel it shares.
          panelData: Array.from({ length: panelCount }, (_, i) => [
            ...(i < metrics.length ? [seriesDataByKey.get(metrics[i]?.key as string)] : []),
            ...(hasTerrain && i === terrainAxisIndex
              ? [seriesDataByKey.get(TERRAIN_SERIES_ID)]
              : []),
          ]),
        })
      : {
          // `top` leaves room for the lap/split marker labels above the plot.
          grid: { left: 55, right: axisColors.length > 1 ? 55 : 20, top: 20, bottom: 56 },
          xAxis: timeAxisBase,
          // Only the first two axes (left + right) are drawn; any further
          // metric keeps its own (hidden) scale, so no two visible axes'
          // labels ever share a side. Compact labels fit the 55px margins.
          yAxis: axisColors.map((axisColor, i) => ({
            type: 'value',
            position: i % 2 === 0 ? 'left' : 'right',
            offset: i >= 2 ? Math.floor((i - 2) / 2 + 1) * 45 : 0,
            show: i < 2,
            scale: true,
            axisLine: { show: i < 2, lineStyle: { color: axisColor } },
            axisLabel: { color: colors.mutedForeground, formatter: formatAxisValue },
            splitLine: { show: i === 0, lineStyle: { color: colors.border } },
          })),
        }

  return {
    backgroundColor: 'transparent',
    // ECharts only animates series under its default `animationThreshold`
    // (2000 points), so short activities faded in while long ones appeared
    // instantly. Off for all: consistent, and an editing tool redraws often.
    animation: false,
    textStyle: { color: colors.foreground, fontFamily: 'inherit' },
    ...layoutParts,
    // Not drawn: the toggles are HTML buttons above the chart
    // (`TimelineLegend`, a swipe-scrollable row -- a canvas legend can only
    // page). The component still owns which series are shown, driven via
    // `legendToggleSelect`. Range selection is driven from the timeline
    // header (`takeGlobalCursor`), not an in-chart toolbox.
    legend: { show: false, data: legendNames },
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: 'line', lineStyle: { color: colors.primary } },
      formatter: stacked
        ? (params: unknown) =>
            formatStackedTooltip(params, tooltipEntries, seriesDataByKey, isSeriesVisible)
        : (params: unknown) => formatTooltip(params, seriesDataByKey),
      backgroundColor: colors.card,
      borderColor: colors.border,
      textStyle: { color: colors.foreground },
    },
    series,
    dataZoom: [
      // throttle: 0 -- ECharts' default (100ms) drops the pan deltas of
      // mouse moves arriving faster than that instead of accumulating them,
      // so a quick drag only moved the chart a fraction of the cursor travel.
      {
        type: 'inside',
        xAxisIndex: 'all',
        filterMode: 'none',
        throttle: 0,
      },
      {
        type: 'slider',
        xAxisIndex: 'all',
        filterMode: 'none',
        bottom: 8,
        height: 16,
        borderColor: colors.border,
        fillerColor: withAlpha(colors.primary, 0.2),
        handleStyle: { color: colors.primary },
        textStyle: { color: colors.mutedForeground },
      },
    ],
    // A `brush` component makes ECharts create a toolbox with its default
    // rect / polygon / keep / clear icons. The timeline header drives the
    // brush instead (`takeGlobalCursor`), so keep that toolbox hidden.
    toolbox: { show: false },
    brush: {
      // Stacked: a range can be dragged out in any panel (all share time).
      xAxisIndex: stacked ? 'all' : 0,
      brushType: 'lineX',
      throttleType: 'debounce',
      throttleDelay: 150,
      brushStyle: { color: withAlpha(colors.accent, 0.6), borderColor: colors.primary },
    },
  }
}
